import { useEffect, useRef, useCallback, useState } from 'react';
import { FunctionEntry, PhysicsObject, GraphSettings } from '@/types/simulation';
import { computeGraphPoints, parseAndEvaluate } from '@/utils/mathParser';
import { TRIANGLE_SCALE } from '@/utils/physics';
import { ThemeMode } from '@/contexts/ThemeContext';

interface Props {
  functions: FunctionEntry[];
  objects: PhysicsObject[];
  settings: GraphSettings;
  showTrails: boolean;
  selectedObjectId: string | null;
  onObjectSelect: (id: string) => void;
  onObjectDrag: (id: string, wx: number, wy: number) => void;
  theme?: ThemeMode;
}

interface Camera {
  rotX: number;
  rotY: number;
  zoom: number;
  panX: number; // world-space pan offset X
  panY: number; // world-space pan offset Y
}

// Project 3D world coords to 2D canvas coords
function project(
  wx: number,
  wy: number,
  wz: number,
  camera: Camera,
  cx: number,
  cy: number
): { x: number; y: number; depth: number } {
  // Apply pan offset
  const px = wx - camera.panX;
  const py = wy - camera.panY;

  // Rotate around Y axis
  const cosY = Math.cos(camera.rotY);
  const sinY = Math.sin(camera.rotY);
  const rx1 = px * cosY - wz * sinY;
  const rz1 = px * sinY + wz * cosY;

  // Rotate around X axis
  const cosX = Math.cos(camera.rotX);
  const sinX = Math.sin(camera.rotX);
  const ry2 = py * cosX - rz1 * sinX;
  const rz2 = py * sinX + rz1 * cosX;

  // Orthographic-style projection scaled by zoom
  // Using zoom directly as a linear scale (no perspective distortion on zoom)
  const screenScale = 60 * camera.zoom;

  return {
    x: cx + rx1 * screenScale,
    y: cy - ry2 * screenScale,
    depth: rz2,
  };
}

function get3DPalette(theme: ThemeMode) {
  if (theme === 'dark') return {
    bgInner: '#141428', bgOuter: '#050508',
    grid: 'rgba(255,255,255,0.06)',
    axisX: 'rgba(255,80,80,0.7)',   axisY: 'rgba(80,255,120,0.7)',  axisZ: 'rgba(80,160,255,0.7)',
    axisLabelX: 'rgba(255,100,100,0.8)', axisLabelY: 'rgba(80,255,120,0.8)', axisLabelZ: 'rgba(80,160,255,0.8)',
    tickLabel: 'rgba(255,255,255,0.35)',
  };
  if (theme === 'light') return {
    bgInner: '#f4f6ff', bgOuter: '#e8ecf8',
    grid: 'rgba(0,0,0,0.07)',
    axisX: 'rgba(200,40,40,0.7)',   axisY: 'rgba(30,140,60,0.7)',  axisZ: 'rgba(20,100,200,0.7)',
    axisLabelX: 'rgba(180,30,30,0.9)', axisLabelY: 'rgba(20,130,50,0.9)', axisLabelZ: 'rgba(10,90,190,0.9)',
    tickLabel: 'rgba(0,0,0,0.45)',
  };
  return {
    bgInner: '#ffffff', bgOuter: '#f5f5ff',
    grid: 'rgba(0,0,0,0.08)',
    axisX: 'rgba(180,20,20,0.8)',   axisY: 'rgba(20,120,40,0.8)',   axisZ: 'rgba(10,80,180,0.8)',
    axisLabelX: 'rgba(160,10,10,1)', axisLabelY: 'rgba(10,110,30,1)', axisLabelZ: 'rgba(0,70,170,1)',
    tickLabel: 'rgba(0,0,0,0.60)',
  };
}

// Adaptive nice step for tick labels
function niceStep(range: number, targetCount = 8): number {
  const raw = range / targetCount;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  let nice: number;
  if (norm < 1.5) nice = 1;
  else if (norm < 3.5) nice = 2;
  else if (norm < 7.5) nice = 5;
  else nice = 10;
  return nice * mag;
}

// ---- Viewport Gizmo (Blender-style axis indicator) ----
function drawGizmo(
  canvas: HTMLCanvasElement,
  cam: Camera,
  theme: ThemeMode
) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const S = canvas.width; // gizmo canvas is square
  const cx = S / 2;
  const cy = S / 2;
  const R = S * 0.36; // arm length in pixels

  ctx.clearRect(0, 0, S, S);

  // Background circle
  const isDark = theme === 'dark';
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, S / 2 - 1, 0, Math.PI * 2);
  ctx.fillStyle = isDark ? 'rgba(10,10,22,0.72)' : 'rgba(240,242,255,0.80)';
  ctx.fill();
  ctx.strokeStyle = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.10)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();

  // Rotate a unit vector by camera angles
  const rotVec = (lx: number, ly: number, lz: number) => {
    const cosY = Math.cos(cam.rotY), sinY = Math.sin(cam.rotY);
    const rx1 = lx * cosY - lz * sinY;
    const rz1 = lx * sinY + lz * cosY;
    const cosX = Math.cos(cam.rotX), sinX = Math.sin(cam.rotX);
    const ry2 = ly * cosX - rz1 * sinX;
    const rz2 = ly * sinX + rz1 * cosX;
    return { x: rx1, y: -ry2, z: rz2 };
  };

  // Axes: [localX, localY, localZ, posLabel, negLabel, posColor, negColor]
  type AxisDef = [number, number, number, string, string, string, string];
  const axes: AxisDef[] = [
    [1, 0, 0, 'X', '-X', '#e05555', '#7a2222'],
    [0, 1, 0, 'Y', '-Y', '#55c455', '#226622'],
    [0, 0, 1, 'Z', '-Z', '#5599e0', '#224477'],
  ];

  // Project each axis tip
  type AxisProj = {
    px: number; py: number; pz: number;
    nx: number; ny: number; nz: number;
    posLabel: string; negLabel: string;
    posColor: string; negColor: string;
  };
  const projected: AxisProj[] = axes.map(([lx, ly, lz, pl, nl, pc, nc]) => {
    const pos = rotVec(lx, ly, lz);
    const neg = rotVec(-lx, -ly, -lz);
    return {
      px: cx + pos.x * R, py: cy + pos.y * R, pz: pos.z,
      nx: cx + neg.x * R, ny: cy + neg.y * R, nz: neg.z,
      posLabel: pl, negLabel: nl, posColor: pc, negColor: nc,
    };
  });

  // Sort: draw back-facing (negative) first, then positive on top
  const allEnds: { x: number; y: number; z: number; label: string; color: string; isPos: boolean }[] = [];
  projected.forEach((ax) => {
    allEnds.push({ x: ax.px, y: ax.py, z: ax.pz, label: ax.posLabel, color: ax.posColor, isPos: true });
    allEnds.push({ x: ax.nx, y: ax.ny, z: ax.nz, label: ax.negLabel, color: ax.negColor, isPos: false });
  });
  allEnds.sort((a, b) => a.z - b.z); // back to front

  // Draw lines first (all, thin)
  projected.forEach((ax) => {
    ctx.save();
    ctx.strokeStyle = ax.posColor;
    ctx.lineWidth = 2.5;
    ctx.globalAlpha = 0.9;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(ax.px, ax.py);
    ctx.stroke();
    // Negative arm (dimmer, dashed)
    ctx.strokeStyle = ax.negColor;
    ctx.lineWidth = 1.5;
    ctx.globalAlpha = 0.45;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(ax.nx, ax.ny);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  });

  // Draw endpoint spheres + labels (depth sorted)
  allEnds.forEach(({ x, y, z, label, color, isPos }) => {
    const ballR = isPos ? S * 0.115 : S * 0.072;
    const alpha = isPos ? 1.0 : 0.45;
    ctx.save();
    ctx.globalAlpha = alpha;

    // Ball gradient
    const grad = ctx.createRadialGradient(x - ballR * 0.3, y - ballR * 0.3, ballR * 0.05, x, y, ballR);
    grad.addColorStop(0, lightenColor(color, 60));
    grad.addColorStop(0.5, color);
    grad.addColorStop(1, darkenColor(color, 40));
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, ballR, 0, Math.PI * 2);
    ctx.fill();

    // Label
    if (isPos) {
      ctx.fillStyle = '#ffffff';
      ctx.font = `bold ${Math.round(S * 0.16)}px Inter, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 2;
      ctx.fillText(label, x, y + 0.5);
    }
    ctx.restore();
  });

  // Center dot
  ctx.save();
  ctx.fillStyle = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.35)';
  ctx.beginPath();
  ctx.arc(cx, cy, 3.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function lightenColor(hex: string, amt: number): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, (n >> 16) + amt);
  const g = Math.min(255, ((n >> 8) & 0xff) + amt);
  const b = Math.min(255, (n & 0xff) + amt);
  return `rgb(${r},${g},${b})`;
}
function darkenColor(hex: string, amt: number): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, (n >> 16) - amt);
  const g = Math.max(0, ((n >> 8) & 0xff) - amt);
  const b = Math.max(0, (n & 0xff) - amt);
  return `rgb(${r},${g},${b})`;
}

export default function GraphCanvas3D({
  functions,
  objects,
  settings,
  showTrails,
  selectedObjectId,
  onObjectSelect,
  onObjectDrag,
  theme = 'dark',
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cameraRef = useRef<Camera>({ rotX: 0.45, rotY: 0.5, zoom: 1, panX: 0, panY: 0 });
  const draggingRef = useRef<{
    mode: 'rotate' | 'pan';
    startX: number; startY: number;
    startRotX: number; startRotY: number;
    startPanX: number; startPanY: number;
  } | null>(null);
  const didMoveRef = useRef(false);
  const animRef = useRef<number | null>(null);
  const gizmoCanvasRef = useRef<HTMLCanvasElement>(null);
  const [tracking, setTracking] = useState(false);
  const trackingRef = useRef(false);

  // Keep trackingRef in sync
  useEffect(() => { trackingRef.current = tracking; }, [tracking]);

  // When tracking is on, follow selected object
  const objectsRef = useRef(objects);
  useEffect(() => { objectsRef.current = objects; }, [objects]);

  const drawScene = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const W = canvas.width;
    const H = canvas.height;
    const cx = W / 2;
    const cy = H / 2;
    const cam = cameraRef.current;
    const pal = get3DPalette(theme);

    // Tracking: move panX/panY to follow selected object
    if (trackingRef.current && selectedObjectId) {
      const target = objectsRef.current.find((o) => o.id === selectedObjectId);
      if (target) {
        cam.panX += (target.x - cam.panX) * 0.12;
        cam.panY += (target.y - cam.panY) * 0.12;
      }
    }

    ctx.clearRect(0, 0, W, H);

    // Background
    const bg = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(W, H) * 0.7);
    bg.addColorStop(0, pal.bgInner);
    bg.addColorStop(1, pal.bgOuter);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    const { xMin, xMax, yMin, yMax } = settings;

    // Adaptive grid step based on current zoom
    const worldRangeX = xMax - xMin;
    const screenUnitsX = worldRangeX * cam.zoom * 60; // approx screen pixels for full range
    const targetGridPx = 70; // desired pixel gap between grid lines
    const targetCount = Math.max(3, Math.round(screenUnitsX / targetGridPx));
    const gridStep = niceStep(worldRangeX, targetCount);

    const zHalf = (xMax - xMin) / 2;
    const zMin = -zHalf;
    const zMax = zHalf;
    const floorY = yMin - (yMax - yMin) * 0.05;

    // ---- Grid floor (XZ plane at floor level) ----
    ctx.save();
    ctx.strokeStyle = pal.grid;
    ctx.lineWidth = 1;
    for (let gx = Math.ceil(xMin / gridStep) * gridStep; gx <= xMax + 0.001; gx += gridStep) {
      const p1 = project(gx, floorY, zMin, cam, cx, cy);
      const p2 = project(gx, floorY, zMax, cam, cx, cy);
      ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(p2.x, p2.y); ctx.stroke();
    }
    const zGridStep = niceStep(zMax - zMin, targetCount);
    for (let gz = Math.ceil(zMin / zGridStep) * zGridStep; gz <= zMax + 0.001; gz += zGridStep) {
      const p1 = project(xMin, floorY, gz, cam, cx, cy);
      const p2 = project(xMax, floorY, gz, cam, cx, cy);
      ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(p2.x, p2.y); ctx.stroke();
    }
    ctx.restore();

    // ---- X axis ----
    ctx.save();
    ctx.strokeStyle = pal.axisX; ctx.lineWidth = 2;
    const axX1 = project(xMin, 0, 0, cam, cx, cy);
    const axX2 = project(xMax, 0, 0, cam, cx, cy);
    ctx.beginPath(); ctx.moveTo(axX1.x, axX1.y); ctx.lineTo(axX2.x, axX2.y); ctx.stroke();
    ctx.fillStyle = pal.axisLabelX; ctx.font = 'bold 12px Inter';
    ctx.fillText('X', axX2.x + 6, axX2.y);
    ctx.restore();

    // ---- Y axis ----
    ctx.save();
    ctx.strokeStyle = pal.axisY; ctx.lineWidth = 2;
    const axY1 = project(0, yMin, 0, cam, cx, cy);
    const axY2 = project(0, yMax, 0, cam, cx, cy);
    ctx.beginPath(); ctx.moveTo(axY1.x, axY1.y); ctx.lineTo(axY2.x, axY2.y); ctx.stroke();
    ctx.fillStyle = pal.axisLabelY; ctx.font = 'bold 12px Inter';
    ctx.fillText('Y', axY2.x, axY2.y - 8);
    ctx.restore();

    // ---- Z axis ----
    ctx.save();
    ctx.strokeStyle = pal.axisZ; ctx.lineWidth = 2;
    const axZ1 = project(0, 0, zMin, cam, cx, cy);
    const axZ2 = project(0, 0, zMax, cam, cx, cy);
    ctx.beginPath(); ctx.moveTo(axZ1.x, axZ1.y); ctx.lineTo(axZ2.x, axZ2.y); ctx.stroke();
    ctx.fillStyle = pal.axisLabelZ; ctx.font = 'bold 12px Inter';
    ctx.fillText('Z', axZ2.x + 4, axZ2.y);
    ctx.fillStyle = pal.tickLabel; ctx.font = '10px Inter';
    ctx.fillText('-Z', axZ1.x - 4, axZ1.y);
    ctx.restore();

    // ---- Origin dot ----
    ctx.save();
    const originPt = project(0, 0, 0, cam, cx, cy);
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.beginPath(); ctx.arc(originPt.x, originPt.y, 3, 0, Math.PI * 2); ctx.fill();
    ctx.restore();

    // ---- Tick labels on X axis (adaptive) ----
    ctx.save();
    ctx.fillStyle = pal.tickLabel;
    ctx.font = '10px Inter';
    ctx.textAlign = 'center';
    for (let gx = Math.ceil(xMin / gridStep) * gridStep; gx <= xMax + 0.001; gx += gridStep) {
      if (Math.abs(gx) < gridStep * 0.01) continue;
      const p = project(gx, floorY - 0.3, 0, cam, cx, cy);
      const label = Number.isInteger(gx) ? String(gx) : gx.toFixed(1);
      ctx.fillText(label, p.x, p.y);
    }
    // Z tick labels
    for (let gz = Math.ceil(zMin / zGridStep) * zGridStep; gz <= zMax + 0.001; gz += zGridStep) {
      if (Math.abs(gz) < zGridStep * 0.01) continue;
      const p = project(xMin - 0.3, floorY - 0.3, gz, cam, cx, cy);
      const label = Number.isInteger(gz) ? String(gz) : gz.toFixed(1);
      ctx.fillText(label, p.x, p.y);
    }
    // Y tick labels
    const yGridStep = niceStep(yMax - yMin, targetCount);
    ctx.textAlign = 'right';
    for (let gy = Math.ceil(yMin / yGridStep) * yGridStep; gy <= yMax + 0.001; gy += yGridStep) {
      if (Math.abs(gy) < yGridStep * 0.01) continue;
      const p = project(xMin - 0.3, gy, 0, cam, cx, cy);
      const label = Number.isInteger(gy) ? String(gy) : gy.toFixed(1);
      ctx.fillText(label, p.x - 4, p.y);
    }
    ctx.restore();

    // ---- 3D surface mesh ----
    const steps = 60;
    const zSteps = 24;
    const zStep2 = (zMax - zMin) / zSteps;
    functions
      .filter((f) => f.visible && !f.error && f.expression.trim())
      .forEach((fn) => {
        const xStep = (xMax - xMin) / steps;
        for (let zi = 0; zi <= zSteps; zi++) {
          const z = zMin + zi * zStep2;
          ctx.save();
          ctx.strokeStyle = fn.color + '55';
          ctx.lineWidth = 1;
          ctx.beginPath();
          let first = true;
          for (let xi = 0; xi <= steps; xi++) {
            const wx = xMin + xi * xStep;
            const wy = parseAndEvaluate(fn.expression, wx);
            if (isNaN(wy) || !isFinite(wy)) { first = true; continue; }
            const p = project(wx, wy, z, cam, cx, cy);
            if (first) { ctx.moveTo(p.x, p.y); first = false; }
            else ctx.lineTo(p.x, p.y);
          }
          ctx.stroke();
          ctx.restore();
        }
        for (let xi = 0; xi <= steps; xi += 4) {
          const wx = xMin + xi * xStep;
          const wy = parseAndEvaluate(fn.expression, wx);
          if (isNaN(wy) || !isFinite(wy)) continue;
          ctx.save();
          ctx.strokeStyle = fn.color + '33';
          ctx.lineWidth = 0.8;
          ctx.beginPath();
          for (let zi = 0; zi <= zSteps; zi++) {
            const z = zMin + zi * zStep2;
            const p = project(wx, wy, z, cam, cx, cy);
            if (zi === 0) ctx.moveTo(p.x, p.y);
            else ctx.lineTo(p.x, p.y);
          }
          ctx.stroke();
          ctx.restore();
        }
        ctx.save();
        ctx.strokeStyle = fn.color;
        ctx.lineWidth = 3;
        if (theme === 'dark') { ctx.shadowColor = fn.color; ctx.shadowBlur = 10; }
        ctx.beginPath();
        let penDown2 = false;
        const pts = computeGraphPoints(fn.expression, xMin, xMax, 400);
        pts.forEach(({ x: wx, y: wy }) => {
          if (isNaN(wy) || !isFinite(wy)) { penDown2 = false; return; }
          const p = project(wx, wy, 0, cam, cx, cy);
          if (!penDown2) { ctx.moveTo(p.x, p.y); penDown2 = true; }
          else ctx.lineTo(p.x, p.y);
        });
        ctx.stroke();
        ctx.restore();
      });

    // ---- Trails (z=0 平面上に描画 — 物体の実際のXY軌跡) ----
    if (showTrails) {
      objects.forEach((obj) => {
        if (obj.trail.length < 2) return;
        const trailLen = obj.trail.length;

        ctx.save();

        // ── 軌跡ライン（z=0 固定） ──
        for (let i = 1; i < trailLen; i++) {
          const t1 = i / (trailLen - 1);
          const alpha = 0.12 + t1 * 0.88;
          const hexAlpha = Math.round(alpha * 210).toString(16).padStart(2, '0');

          const p0 = project(obj.trail[i - 1].x, obj.trail[i - 1].y, 0, cam, cx, cy);
          const p1 = project(obj.trail[i].x,     obj.trail[i].y,     0, cam, cx, cy);

          ctx.strokeStyle = obj.color + hexAlpha;
          ctx.lineWidth = 1.2 + t1 * 1.8; // 新しい点ほど太く
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(p0.x, p0.y);
          ctx.lineTo(p1.x, p1.y);
          ctx.stroke();
        }

        // ── 床面への影（y=floorY, z=0） ──
        ctx.globalAlpha = 0.15;
        ctx.strokeStyle = obj.color;
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 4]);
        for (let i = 1; i < trailLen; i++) {
          const p0 = project(obj.trail[i - 1].x, floorY, 0, cam, cx, cy);
          const p1 = project(obj.trail[i].x,     floorY, 0, cam, cx, cy);
          ctx.beginPath();
          ctx.moveTo(p0.x, p0.y);
          ctx.lineTo(p1.x, p1.y);
          ctx.stroke();
        }
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;

        // ── 最新点から床への垂直ドロップライン（z=0） ──
        if (trailLen > 0) {
          const last = obj.trail[trailLen - 1];
          const pTop = project(last.x, last.y,   0, cam, cx, cy);
          const pBot = project(last.x, floorY,   0, cam, cx, cy);
          ctx.globalAlpha = 0.22;
          ctx.strokeStyle = obj.color;
          ctx.lineWidth = 1;
          ctx.setLineDash([2, 3]);
          ctx.beginPath();
          ctx.moveTo(pTop.x, pTop.y);
          ctx.lineTo(pBot.x, pBot.y);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.globalAlpha = 1;
        }

        ctx.restore();
      });
    }

    // ---- Objects — Billboard style (always face the camera) ----
    // 物体は常にスクリーン平面に正対して描画（カメラ回転の影響を受けない）
    // 2Dの angle（物理回転）はスクリーン上でそのまま回転として表現

    // depth順でソートするため、まず全物体のdepthを計算
    const objectsWithDepth = objects
      .filter((obj) => {
        const margin = 2;
        return !(obj.x < xMin - margin || obj.x > xMax + margin ||
          obj.y < yMin - margin || obj.y > yMax + margin);
      })
      .map((obj) => {
        const p = project(obj.x, obj.y, 0, cam, cx, cy);
        return { obj, screenX: p.x, screenY: p.y, depth: p.depth };
      })
      .sort((a, b) => b.depth - a.depth); // 奥から手前の順に描画

    // ---- Gizmo ----
    if (gizmoCanvasRef.current) {
      drawGizmo(gizmoCanvasRef.current, cam, theme);
    }

    objectsWithDepth.forEach(({ obj, screenX, screenY }) => {
      const isSelected = obj.id === selectedObjectId;
      const radiusWorld = obj.size / 2;
      const screenScale = 60 * cam.zoom;
      const r = radiusWorld * screenScale; // スクリーンピクセル半径

      ctx.save();
      ctx.translate(screenX, screenY);
      // 2Dの物理回転角をそのままスクリーン上に適用（ビルボードなのでカメラに依存しない）
      ctx.rotate(obj.angle);

      if (isSelected && theme === 'dark') {
        ctx.shadowColor = obj.color;
        ctx.shadowBlur = 22;
      }

      const strokeCol = isSelected ? 'rgba(255,255,255,0.90)' : 'rgba(255,255,255,0.25)';
      const strokeW = isSelected ? 2.5 : 1;

      if (obj.shape === 'circle') {
        // 球: グラデーション付き円 + ハイライト
        const grad = ctx.createRadialGradient(-r * 0.3, -r * 0.3, r * 0.05, 0, 0, r);
        grad.addColorStop(0, 'rgba(255,255,255,0.55)');
        grad.addColorStop(0.35, obj.color);
        grad.addColorStop(1, shadeColor(obj.color, -40));
        ctx.fillStyle = grad;
        ctx.strokeStyle = strokeCol;
        ctx.lineWidth = strokeW;
        ctx.beginPath();
        ctx.arc(0, 0, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        // 回転ライン
        ctx.save();
        ctx.strokeStyle = 'rgba(255,255,255,0.65)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(r, 0);
        ctx.stroke();
        // ハイライト
        ctx.fillStyle = 'rgba(255,255,255,0.22)';
        ctx.beginPath();
        ctx.arc(-r * 0.27, -r * 0.27, r * 0.32, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

      } else if (obj.shape === 'square') {
        // 正方形: 面ごとに明暗をつけてシンプルな立体感
        // メイン面
        ctx.fillStyle = obj.color;
        ctx.strokeStyle = strokeCol;
        ctx.lineWidth = strokeW;
        ctx.beginPath();
        ctx.rect(-r, -r, r * 2, r * 2);
        ctx.fill();
        ctx.stroke();
        // 上面ハイライト
        ctx.fillStyle = 'rgba(255,255,255,0.18)';
        ctx.beginPath();
        ctx.rect(-r, -r, r * 2, r * 0.55);
        ctx.fill();
        // 右下シャドウ
        ctx.fillStyle = 'rgba(0,0,0,0.18)';
        ctx.beginPath();
        ctx.rect(r * 0.3, r * 0.3, r * 0.7, r * 0.7);
        ctx.fill();
        // 角のハイライト
        ctx.fillStyle = 'rgba(255,255,255,0.55)';
        ctx.beginPath();
        ctx.arc(r * 0.6, -r * 0.6, r * 0.13, 0, Math.PI * 2);
        ctx.fill();
        // 内側の十字線
        ctx.save();
        ctx.strokeStyle = 'rgba(255,255,255,0.15)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(-r, 0); ctx.lineTo(r, 0);
        ctx.moveTo(0, -r); ctx.lineTo(0, r);
        ctx.stroke();
        ctx.restore();

      } else if (obj.shape === 'triangle') {
        // 描画サイズをTRIANGLE_SCALEで拡大（物理頂点と完全一致）
        const tr = r * TRIANGLE_SCALE;
        ctx.strokeStyle = strokeCol;
        ctx.lineWidth = strokeW;
        ctx.fillStyle = obj.color;
        ctx.beginPath();
        ctx.moveTo(0, -tr);
        ctx.lineTo(tr * 0.866, tr * 0.5);
        ctx.lineTo(-tr * 0.866, tr * 0.5);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = 'rgba(255,255,255,0.20)';
        ctx.beginPath();
        ctx.moveTo(0, -tr);
        ctx.lineTo(tr * 0.43, -tr * 0.1);
        ctx.lineTo(-tr * 0.43, -tr * 0.1);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,0.55)';
        ctx.beginPath();
        ctx.arc(0, -tr * 0.72, tr * 0.11, 0, Math.PI * 2);
        ctx.fill();
        ctx.save();
        ctx.strokeStyle = 'rgba(255,255,255,0.15)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, -tr * 0.5);
        ctx.lineTo(tr * 0.433, tr * 0.25);
        ctx.lineTo(-tr * 0.433, tr * 0.25);
        ctx.closePath();
        ctx.stroke();
        ctx.restore();
      }

      ctx.restore();

      // 選択時の速度矢印
      if (isSelected) {
        const vScale = 20;
        const vex = screenX + obj.vx * vScale;
        const vey = screenY - obj.vy * vScale;
        const dx = vex - screenX;
        const dy = vey - screenY;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len > 5) {
          const angle3d = Math.atan2(dy, dx);
          ctx.save();
          ctx.strokeStyle = theme === 'picture' ? '#0070a0' : '#00d9ff';
          ctx.fillStyle = theme === 'picture' ? '#0070a0' : '#00d9ff';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(screenX, screenY);
          ctx.lineTo(vex, vey);
          ctx.stroke();
          const headLen = 8;
          ctx.beginPath();
          ctx.moveTo(vex, vey);
          ctx.lineTo(vex - headLen * Math.cos(angle3d - Math.PI / 6), vey - headLen * Math.sin(angle3d - Math.PI / 6));
          ctx.lineTo(vex - headLen * Math.cos(angle3d + Math.PI / 6), vey - headLen * Math.sin(angle3d + Math.PI / 6));
          ctx.closePath();
          ctx.fill();
          ctx.restore();
        }
      }
    });
  }, [functions, objects, settings, showTrails, selectedObjectId, theme]);

  // Resize observer
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (canvasRef.current) {
        canvasRef.current.width = el.clientWidth;
        canvasRef.current.height = el.clientHeight;
      }
      drawScene();
    });
    ro.observe(el);
    if (canvasRef.current) {
      canvasRef.current.width = el.clientWidth;
      canvasRef.current.height = el.clientHeight;
    }
    return () => ro.disconnect();
  }, [drawScene]);

  useEffect(() => {
    const loop = () => {
      drawScene();
      animRef.current = requestAnimationFrame(loop);
    };
    animRef.current = requestAnimationFrame(loop);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [drawScene]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0) {
      // Right-click or shift+drag = pan, left drag = rotate
      const mode = e.shiftKey ? 'pan' : 'rotate';
      draggingRef.current = {
        mode,
        startX: e.clientX, startY: e.clientY,
        startRotX: cameraRef.current.rotX, startRotY: cameraRef.current.rotY,
        startPanX: cameraRef.current.panX, startPanY: cameraRef.current.panY,
      };
      didMoveRef.current = false;
    } else if (e.button === 2) {
      e.preventDefault();
      draggingRef.current = {
        mode: 'pan',
        startX: e.clientX, startY: e.clientY,
        startRotX: cameraRef.current.rotX, startRotY: cameraRef.current.rotY,
        startPanX: cameraRef.current.panX, startPanY: cameraRef.current.panY,
      };
      didMoveRef.current = false;
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!draggingRef.current) return;
    const dx = e.clientX - draggingRef.current.startX;
    const dy = e.clientY - draggingRef.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) didMoveRef.current = true;

    if (draggingRef.current.mode === 'rotate') {
      cameraRef.current.rotY = draggingRef.current.startRotY - dx * 0.008;
      cameraRef.current.rotX = Math.max(-1.2, Math.min(1.2, draggingRef.current.startRotX + dy * 0.008));
    } else {
      // Pan: move in screen space, convert to world space
      const panSpeed = 1 / (cameraRef.current.zoom * 60);
      cameraRef.current.panX = draggingRef.current.startPanX - dx * panSpeed;
      cameraRef.current.panY = draggingRef.current.startPanY + dy * panSpeed;
      // Disable tracking when manually panning
      if (trackingRef.current) setTracking(false);
    }
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!didMoveRef.current && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const ccx = canvasRef.current.width / 2;
      const ccy = canvasRef.current.height / 2;
      const cam = cameraRef.current;
      let hitId: string | null = null;
      let minDist = Infinity;
      objects.forEach((obj) => {
        const p = project(obj.x, obj.y, 0, cam, ccx, ccy);
        const dist = Math.sqrt((mx - p.x) ** 2 + (my - p.y) ** 2);
        const hitRadius = (obj.size / 2) * 60 * cam.zoom + 4;
        if (dist < hitRadius && dist < minDist) {
          minDist = dist;
          hitId = obj.id;
        }
      });
      if (hitId) onObjectSelect(hitId);
    }
    draggingRef.current = null;
    didMoveRef.current = false;
  };

  const handleWheel = (e: React.WheelEvent) => {
    cameraRef.current.zoom = Math.max(0.1, Math.min(8, cameraRef.current.zoom * (e.deltaY < 0 ? 1.1 : 0.91)));
  };

  const handleResetCamera = () => {
    cameraRef.current = { rotX: 0.45, rotY: 0.5, zoom: 1, panX: 0, panY: 0 };
    setTracking(false);
  };

  const handleToggleTracking = () => {
    const next = !tracking;
    setTracking(next);
    // Snap to target immediately
    if (next && selectedObjectId) {
      const target = objects.find((o) => o.id === selectedObjectId);
      if (target) {
        cameraRef.current.panX = target.x;
        cameraRef.current.panY = target.y;
      }
    }
  };

  const isDark = theme === 'dark';
  const btnBase = `glass-pill ripple-btn w-7 h-7 flex items-center justify-center rounded-full cursor-pointer transition-all hover:scale-110 ${isDark ? 'text-white/50 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`;
  const trackingActive = tracking && !!selectedObjectId;

  return (
    <div
      ref={containerRef}
      className="w-full h-full relative"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
      onContextMenu={(e) => e.preventDefault()}
    >
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        style={{ cursor: draggingRef.current ? (draggingRef.current.mode === 'pan' ? 'grabbing' : 'grabbing') : 'grab' }}
      />

      {/* Controls */}
      <div className="no-print absolute bottom-3 left-3 flex items-center gap-1.5">
        <button onClick={() => { cameraRef.current.zoom = Math.min(8, cameraRef.current.zoom * 1.15); }} className={btnBase} title="ズームイン">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-zoom-in-line text-sm" /></div>
        </button>
        <button onClick={() => { cameraRef.current.zoom = Math.max(0.1, cameraRef.current.zoom * 0.87); }} className={btnBase} title="ズームアウト">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-zoom-out-line text-sm" /></div>
        </button>
        <button onClick={handleResetCamera} className={btnBase} title="視点をリセット">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-focus-3-line text-sm" /></div>
        </button>

        {/* Tracking button */}
        <button
          onClick={handleToggleTracking}
          className={`glass-pill ripple-btn w-7 h-7 flex items-center justify-center rounded-full cursor-pointer transition-all hover:scale-110 ${
            trackingActive
              ? 'text-emerald-400 ring-1 ring-emerald-400/50'
              : isDark ? 'text-white/50 hover:text-white' : 'text-gray-500 hover:text-gray-900'
          }`}
          title={trackingActive ? 'トラッキング中（クリックで解除）' : '選択物体をトラッキング'}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-crosshair-2-line text-sm ${trackingActive ? 'animate-pulse' : ''}`} />
          </div>
        </button>
      </div>

      <div className={`no-print absolute bottom-3 right-3 glass-pill px-3 py-1.5 text-xs pointer-events-none select-none rounded-full ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
        ドラッグ:回転 · Shift+ドラッグ/右クリック:移動 · スクロール:ズーム
      </div>

      <div className={`no-print absolute top-3 left-3 glass-pill px-3 py-1.5 text-xs pointer-events-none select-none rounded-full flex items-center gap-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
        <div className="w-3 h-3 flex items-center justify-center">
          <i className={`ri-rotate-lock-line ${isDark ? 'text-cyan-400' : 'text-sky-600'}`} />
        </div>
        3D モード
      </div>

      {/* Viewport Gizmo — top right */}
      <canvas
        ref={gizmoCanvasRef}
        width={88}
        height={88}
        className="no-print absolute pointer-events-none select-none"
        style={{
          top: trackingActive ? '52px' : '12px',
          right: '12px',
          width: 88,
          height: 88,
          borderRadius: '50%',
        }}
      />

      {/* Tracking indicator */}
      {trackingActive && (
        <div className="no-print absolute top-3 right-3 glass-pill px-3 py-1.5 text-xs select-none rounded-full flex items-center gap-1.5 text-emerald-400 ring-1 ring-emerald-400/30">
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-crosshair-2-line text-xs animate-pulse" />
          </div>
          トラッキング中
        </div>
      )}
    </div>
  );
}

function shadeColor(hex: string, amount: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, Math.min(255, (num >> 16) + amount));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amount));
  const b = Math.max(0, Math.min(255, (num & 0xff) + amount));
  return `rgb(${r},${g},${b})`;
}
